import ClientPanel from "./ClientPanel";
import {
  DeviceAttribute,
  DeviceAttributeType,
  DevicePowerUsageType,
  getDeviceAttributeTypeFromDevicePowerUsageType,
  getDeviceAttributeTypeFromDeviceTemplate,
  getDevicePowerUsageNameForDefaultTemplateCreation,
} from "./DeviceAttribute";
import { DeviceCategoryTemplate } from "./DeviceCategoryTemplate";
import { DeviceOverview } from "./DeviceOverview";
import { DeviceTemplate } from "./DeviceTemplate";
import {
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
} from "./IresDevice";
import { isHyDryMiniSerial } from "./ComBox";
import { getPanelTypeFromSerialNumber, PanelType } from "./Panel";
import { db } from "@/main";
import { getClientId, getClientRef } from "@/data/repository/ClientRepository";
import { generateRandomUuid } from "@/utils/Utils";
import { DeviceAttributeContainer } from "@/data/models/DeviceAttributeContainer";
import { formatPowerConsumption } from "@/data/models/BaseDeviceInfo";

export const HY_DRY_PANEL_ID = "00000000-0000-0000-0000-000000000001";
export const SLIM_PANEL_ID = "00000000-0000-0000-0000-000000000002";
export const EDGE_PANEL_ID = "00000000-0000-0000-0000-000000000003";
export const COM_BOX_ID = "00000000-0000-0000-0000-000000000004";
export const HYDRY_MINI_ID = "00000000-0000-0000-0000-00000000000A";

export const UNKNOWN_TEMPLATE_1_ID = "00000000-0000-0000-0000-000000000005";
export const UNKNOWN_TEMPLATE_2_ID = "00000000-0000-0000-0000-000000000006";
export const UNKNOWN_TEMPLATE_3_ID = "00000000-0000-0000-0000-000000000007";
export const UNKNOWN_TEMPLATE_4_ID = "00000000-0000-0000-0000-000000000008";
export const UNKNOWN_TEMPLATE_5_ID = "00000000-0000-0000-0000-000000000009";

export const CATEGORY_HY_DRY_ID = "00000000-0000-0000-0000-000000000000";
export const CATEGORY_DEHUMIDIFICATION = "00000000-0000-0000-0000-000000000001";
export const CATEGORY_INSULATION_DEHUMIDIFICATION =
  "00000000-0000-0000-0000-000000000002";
export const CATEGORY_VENTILATION = "00000000-0000-0000-0000-000000000003";
export const CATEGORY_AIR_CLEANER = "00000000-0000-0000-0000-000000000004";
export const CATEGORY_OTHER = "00000000-0000-0000-0000-000000000005";
export const CATEGORY_OTHER_INFRARED = "00000000-0000-0000-0000-000000000006";
export const CATEGORY_COM_BOX_ID = "00000000-0000-0000-0000-000000000007";

export const CATEGORY_UNKNOWN_ID = "00000000-0000-0000-0000-000000000008";

const IRES_CATEGORIES = [CATEGORY_HY_DRY_ID, CATEGORY_COM_BOX_ID];
const IRES_TEMPLATES = [
  HY_DRY_PANEL_ID,
  SLIM_PANEL_ID,
  EDGE_PANEL_ID,
  COM_BOX_ID,
  HYDRY_MINI_ID,
];

/**
 * Interface representing the device categories and templates JSON structure.
 */
export interface DeviceCategoriesAndTemplates {
  deviceSettingsJson: string;
}

/**
 * JSON structure for storing device settings, including attributes and categories.
 */
export interface DeviceSettingsJson {
  deviceAttributes: DeviceAttribute[];
  deviceCategories: DeviceCategoryTemplate[];
}

/**
 * Retrieves a predefined constant UUID for a device template based on the type of device power usage.
 * This function is designed to provide unique but static identifiers for placeholder or "unknown"
 * templates that might be used in various system operations when specific details are not available.
 * The UUIDs are defined as constants to ensure consistency and to ease maintenance.
 *
 * @param {DevicePowerUsageType} devicePowerUsageType - The type of power usage for which an ID is needed.
 * @returns {string} A constant UUID that uniquely identifies a template for the given power usage type.
 */
function getUnknowTemplateId(devicePowerUsageType: DevicePowerUsageType) {
  switch (devicePowerUsageType) {
    case DevicePowerUsageType.OPERATING_HOURS:
      return UNKNOWN_TEMPLATE_2_ID;
    case DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return UNKNOWN_TEMPLATE_3_ID;
    case DevicePowerUsageType.ELECTRICITY_METER:
      return UNKNOWN_TEMPLATE_4_ID;
    case DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES:
      return UNKNOWN_TEMPLATE_5_ID;
    default:
      return UNKNOWN_TEMPLATE_1_ID;
  }
}

/**
 * Returns a list of constants representing the IDs for default unknown templates.
 * This list includes the general unknown ID and specific IDs for different device power usage types,
 * facilitating consistent reference throughout the application. These constants help in identifying
 * templates when specific template details are not available or when a fallback is necessary.
 *
 * @returns {string[]} An array of constant UUIDs for unknown device templates.
 */
function getUnknownTemplateIds() {
  return [
    UNKNOWN_TEMPLATE_1_ID,
    UNKNOWN_TEMPLATE_2_ID,
    UNKNOWN_TEMPLATE_3_ID,
    UNKNOWN_TEMPLATE_4_ID,
    UNKNOWN_TEMPLATE_5_ID,
  ];
}

/**
 * Determines whether the given category ID matches the predefined ID for an unknown category.
 *
 * @param {string} categoryId - The category ID to check.
 * @returns {boolean} True if the category ID is the predefined unknown category ID, false otherwise.
 */
export function isUnknownCategory(categoryId: string) {
  return categoryId === CATEGORY_UNKNOWN_ID;
}

/**
 * Checks if the given template ID is one of the predefined IDs for unknown templates.
 *
 * @param {string} templateId - The template ID to check.
 * @returns {boolean} True if the template ID is among the list of unknown template IDs, false otherwise.
 */
export function isUnknownTemplate(templateId: string) {
  return getUnknownTemplateIds().includes(templateId);
}

export function isIresCategory(categoryId: string) {
  return IRES_CATEGORIES.includes(categoryId);
}

export function isIresTemplate(templateId: string) {
  return IRES_TEMPLATES.includes(templateId);
}

export function getCategoryIdFromSerialNumber(serialNumber: string) {
  const deviceCategory = getIresDeviceCategoryFromSerialNumber(serialNumber);

  return deviceCategory === IresDeviceCategory.PANEL
    ? CATEGORY_HY_DRY_ID
    : CATEGORY_COM_BOX_ID;
}

export function getTemplateIdFromSerialNumber(serialNumber: string) {
  const deviceCategory = getIresDeviceCategoryFromSerialNumber(serialNumber);

  if (deviceCategory === IresDeviceCategory.PANEL) {
    const panelType = getPanelTypeFromSerialNumber(serialNumber);

    if (panelType === PanelType.EDGE) {
      return EDGE_PANEL_ID;
    } else if (panelType === PanelType.SLIM) {
      return SLIM_PANEL_ID;
    }

    return HY_DRY_PANEL_ID;
  }

  if (isHyDryMiniSerial(serialNumber)) {
    return HYDRY_MINI_ID;
  }

  return COM_BOX_ID;
}

export function isDeviceOverview(
  device: DeviceOverview | ClientPanel
): device is DeviceOverview {
  return (
    (device as DeviceOverview).categoryId !== undefined ||
    (device as DeviceOverview).templateId !== undefined
  );
}

export function isClientPanel(
  device: DeviceOverview | ClientPanel
): device is ClientPanel {
  return !isDeviceOverview(device);
}

export function createOtherCategoryTemplate(
  name: string,
  id: string
): DeviceCategoryTemplate {
  const OTHER_CATEGORY_TEMPLATE: DeviceCategoryTemplate = {
    categoryName: name,
    id: id,
    deviceTemplates: [],
    isEditable: false,
    sortIndex: -1,
  };

  return OTHER_CATEGORY_TEMPLATE;
}

export function createComBoxTemplate(): DeviceTemplate {
  const COM_BOX_TEMPLATE: DeviceTemplate = {
    deviceName: "HyDry Box",
    id: COM_BOX_ID,
    categoryId: CATEGORY_COM_BOX_ID,
    deviceAttributes: [],
    isEditable: false,
    sortIndex: -1,
  };

  return COM_BOX_TEMPLATE;
}

export function createMiniTemplate(): DeviceTemplate {
  const MINI_TEMPLATE: DeviceTemplate = {
    deviceName: "HyDry Mini",
    id: HYDRY_MINI_ID,
    categoryId: CATEGORY_COM_BOX_ID,
    deviceAttributes: [],
    isEditable: false,
    sortIndex: -1,
  };

  return MINI_TEMPLATE;
}

export function createHyDryDeviceTemplate(
  name: string,
  id: string
): DeviceTemplate {
  const HY_DRY_DEVICE_TEMPLATE: DeviceTemplate = {
    deviceName: name,
    id: id,
    categoryId: CATEGORY_HY_DRY_ID,
    deviceAttributes: [],
    isEditable: false,
    sortIndex: -1,
  };

  return HY_DRY_DEVICE_TEMPLATE;
}

export function createComBoxCategoryTemplate(): DeviceCategoryTemplate {
  const COM_BOX_CATEGORY_TEMPLATE: DeviceCategoryTemplate = {
    categoryName: "HyDry Control",
    id: CATEGORY_COM_BOX_ID,
    deviceTemplates: [],
    isEditable: false,
    sortIndex: -1,
  };

  COM_BOX_CATEGORY_TEMPLATE.deviceTemplates.push(createComBoxTemplate());
  COM_BOX_CATEGORY_TEMPLATE.deviceTemplates.push(createMiniTemplate());

  return COM_BOX_CATEGORY_TEMPLATE;
}

export function createDefaultDeviceAttributes(): DeviceAttribute[] {
  const DEFAULT_DEVICE_ATTRIBUTES: DeviceAttribute[] = [
    {
      deviceAttributeType: DeviceAttributeType.INVENTORY_NUMBER,
      isProjectAssignmentDependent: false,
      canHaveDefaultValue: false,
      canBeSelected: true,
    },
    {
      deviceAttributeType: DeviceAttributeType.COMMENT,
      isProjectAssignmentDependent: false,
      canHaveDefaultValue: false,
      canBeSelected: true,
    },
    {
      deviceAttributeType: DeviceAttributeType.METER_READING,
      isProjectAssignmentDependent: true,
      canHaveDefaultValue: false,
      canBeSelected: true,
    },
    {
      deviceAttributeType:
        DeviceAttributeType.ELECTRICITY_METER_FOR_OTHER_DEVICES,
      isProjectAssignmentDependent: false,
      canHaveDefaultValue: false,
      canBeSelected: true,
    },
    {
      deviceAttributeType: DeviceAttributeType.OPERATING_HOURS,
      isProjectAssignmentDependent: true,
      canHaveDefaultValue: false,
      canBeSelected: true,
    },
    {
      deviceAttributeType: DeviceAttributeType.POWER_CONSUMPTION_PER_HOUR,
      isProjectAssignmentDependent: false,
      canHaveDefaultValue: true,
      canBeSelected: true,
    },
    {
      deviceAttributeType: DeviceAttributeType.OPERATING_HOURS_FROM_ASSIGNMENT,
      isProjectAssignmentDependent: true,
      canHaveDefaultValue: false,
      canBeSelected: false,
    },
  ];

  return DEFAULT_DEVICE_ATTRIBUTES;
}

/**
 * Creates a default device category template with the name "Unknown". This category template is
 * used as a fallback when no other categories match. It includes a set of default device templates
 * based on all available power usage types, each labeled as "Unknown Template" followed by a
 * descriptive name derived from the power usage type. These templates are not editable and serve
 * primarily as placeholders or defaults in systems where a specific categorization is undefined.
 *
 * @returns {DeviceCategoryTemplate} An object representing a device category with default settings,
 * including a list of device templates pre-configured for each power usage type. Each template
 * within the category has predefined attributes based on the power usage type, but these are not
 * editable to ensure they serve as static placeholders.
 */
export function createUnknownCategory(): DeviceCategoryTemplate {
  const unknownCategoryTemplate: DeviceCategoryTemplate = {
    categoryName: "Unknown",
    id: CATEGORY_UNKNOWN_ID,
    deviceTemplates: [],
    isEditable: true,
    sortIndex: -1,
  };

  Object.values(DevicePowerUsageType).forEach((devicePowerUsageType, index) => {
    const template: DeviceTemplate = {
      deviceName: `Unknown Template ${getDevicePowerUsageNameForDefaultTemplateCreation(devicePowerUsageType)}`,
      id: getUnknowTemplateId(devicePowerUsageType),
      categoryId: unknownCategoryTemplate.id,
      deviceAttributes: [], // Initialize empty
      isEditable: false,
      sortIndex: index,
    };

    const deviceAttributeType = getDeviceAttributeTypeFromDevicePowerUsageType(devicePowerUsageType);

    if (deviceAttributeType) {
      template.deviceAttributes.push({
        attributeType: deviceAttributeType
      });

      if (DeviceAttributeType.ELECTRICITY_METER_FOR_OTHER_DEVICES === deviceAttributeType) {
        template.deviceAttributes.push({
          attributeType: DeviceAttributeType.METER_READING
        });
      }
    }

    unknownCategoryTemplate.deviceTemplates.push(template);
  });

  return unknownCategoryTemplate;
}

export function createDefaultDeviceCategories(): DeviceCategoryTemplate[] {
  const deviceCategories: DeviceCategoryTemplate[] = [];

  const hyDryCategoryTemplate: DeviceCategoryTemplate = {
    categoryName: "HyDry Infrarot",
    id: CATEGORY_HY_DRY_ID,
    deviceTemplates: [],
    isEditable: false,
    sortIndex: -1,
  };

  hyDryCategoryTemplate.deviceTemplates.push(
    createHyDryDeviceTemplate("HyDry Panel", HY_DRY_PANEL_ID)
  );
  hyDryCategoryTemplate.deviceTemplates.push(
    createHyDryDeviceTemplate("HyDry Edge", EDGE_PANEL_ID)
  );
  hyDryCategoryTemplate.deviceTemplates.push(
    createHyDryDeviceTemplate("HyDry Slim", SLIM_PANEL_ID)
  );

  deviceCategories.push(hyDryCategoryTemplate);
  deviceCategories.push(createComBoxCategoryTemplate());
  deviceCategories.push(
    createOtherCategoryTemplate("Infrarot - Sonstige", CATEGORY_OTHER_INFRARED)
  );
  deviceCategories.push(
    createOtherCategoryTemplate("Entfeuchtung", CATEGORY_DEHUMIDIFICATION)
  );
  deviceCategories.push(
    createOtherCategoryTemplate(
      "Dämmschichtentfeuchtung",
      CATEGORY_INSULATION_DEHUMIDIFICATION
    )
  );
  deviceCategories.push(
    createOtherCategoryTemplate("Ventilation", CATEGORY_VENTILATION)
  );
  deviceCategories.push(
    createOtherCategoryTemplate("Luftreinigung", CATEGORY_AIR_CLEANER)
  );
  deviceCategories.push(
    createOtherCategoryTemplate("Sonstige", CATEGORY_OTHER)
  );

  const unknownCategoryTemplate = createUnknownCategory();
  deviceCategories.push(unknownCategoryTemplate);

  return deviceCategories;
}

export function createDefaultDeviceSettings(): DeviceSettingsJson {
  const defaultDeviceSettingsJson = {
    deviceAttributes: createDefaultDeviceAttributes(),
    deviceCategories: createDefaultDeviceCategories(),
  };

  return defaultDeviceSettingsJson;
}

export async function saveDeviceSettings(deviceSettings: DeviceSettingsJson) {
  try {
    await db
      .doc(getClientRef())
      .collection("deviceSettings")
      .doc(getClientId())
      .set({ deviceSettingsJson: JSON.stringify(deviceSettings) });
    return true;
  } catch (e) {
    return false;
  }
}

export function getMaxDeviceCategorySortIndex(
  deviceSetting: DeviceSettingsJson
): number {
  let max = -1;

  for (const mDeviceCategory of deviceSetting.deviceCategories) {
    max = Math.max(mDeviceCategory.sortIndex, max);
  }

  return max;
}

export function getMaxDeviceTemplateSortIndex(
  category: DeviceCategoryTemplate
): number {
  let max = -1;
  for (const template of category.deviceTemplates) {
    max = Math.max(template.sortIndex, max);
  }
  return max;
}

export async function editCategoryName(
  categoryName: string,
  categoryId: string,
  deviceSettingsJson: DeviceSettingsJson
) {
  if (!deviceSettingsJson) {
    return false;
  }
  const category = deviceSettingsJson.deviceCategories.find(
    (cat) => cat.id === categoryId
  );
  if (!category) {
    return false;
  }
  category.categoryName = categoryName;
  try {
    await saveDeviceSettings(deviceSettingsJson);
    return true;
  } catch (e) {
    return false;
  }
}

export async function addNewCategory(
  newCategoryName: string,
  deviceSettings: DeviceSettingsJson,
  save = true
) {
  try {
    if (!deviceSettings) {
      return null;
    }
    const newCategory: DeviceCategoryTemplate = {
      categoryName: newCategoryName,
      id: generateRandomUuid(),
      deviceTemplates: [],
      isEditable: true,
      sortIndex: getMaxDeviceCategorySortIndex(deviceSettings) + 1,
    };
    deviceSettings.deviceCategories.push(newCategory);
    if (save) {
      await saveDeviceSettings(deviceSettings);
    }
    return deviceSettings;
  } catch (e) {
    return null;
  }
}

export async function addEditTemplate(
  save = true,
  categoryId: string,
  templateName: string,
  devicePowerUsageType: DevicePowerUsageType,
  deviceSettings: DeviceSettingsJson,
  template?: DeviceTemplate | null,
  powerConsumptionPerHour?: number | null
): Promise<DeviceSettingsJson | null> {
  try {
    const category = deviceSettings.deviceCategories.find(
      (cat) => cat.id == categoryId
    );
    if (!category) {
      return null;
    }
    const newTemplate: DeviceTemplate = {
      categoryId: categoryId,
      deviceName: templateName,
      deviceAttributes: template?.deviceAttributes ?? [],
      isEditable: template?.isEditable ?? true,
      sortIndex: template?.sortIndex ?? getMaxDeviceTemplateSortIndex(category),
      id: template?.id ?? generateRandomUuid(),
    };

    const attributeTypes: DeviceAttributeContainer[] = [];
    switch (devicePowerUsageType) {
      case DevicePowerUsageType.ELECTRICITY_METER:
      case DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES:
        {
          attributeTypes.push({
            attributeType: DeviceAttributeType.METER_READING,
          });
          if (
            devicePowerUsageType ==
            DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES
          ) {
            attributeTypes.push({
              attributeType:
                DeviceAttributeType.ELECTRICITY_METER_FOR_OTHER_DEVICES,
            });
          }
        }
        break;
      case DevicePowerUsageType.OPERATING_HOURS:
        {
          attributeTypes.push({
            attributeType: DeviceAttributeType.OPERATING_HOURS,
          });
        }
        break;
    }
    if (powerConsumptionPerHour) {
      attributeTypes.push({
        defaultValue: formatPowerConsumption(
          powerConsumptionPerHour
        ).toString(),
        attributeType: DeviceAttributeType.POWER_CONSUMPTION_PER_HOUR,
      });
    }
    newTemplate.deviceAttributes = attributeTypes;
    const existingTemplateIndex = category.deviceTemplates.findIndex(
      (temp) => temp.id === newTemplate.id
    );

    if (existingTemplateIndex !== -1) {
      category.deviceTemplates[existingTemplateIndex] = newTemplate;
    } else if (existingTemplateIndex == -1) {
      category.deviceTemplates.push(newTemplate);
    }
    if (save) {
      await saveDeviceSettings(deviceSettings);
    }
    return deviceSettings;
  } catch (e) {
    return null;
  }
}

export async function removeCategory(
  categoryId: string,
  deviceSettings: DeviceSettingsJson
) {
  const newSettings = deviceSettings;
  try {
    newSettings.deviceCategories = newSettings.deviceCategories.filter(
      (cat) => cat.id !== categoryId
    );
    await saveDeviceSettings(newSettings);
    return true;
  } catch (e) {
    return false;
  }
}

export async function removeTemplate(
  categoryId: string,
  templateId: string,
  deviceSettings: DeviceSettingsJson
) {
  const newSettings = deviceSettings;
  const categories = newSettings.deviceCategories;
  const category = categories.find((cat) => cat.id == categoryId);
  if (!category) {
    return false;
  }
  try {
    category.deviceTemplates = category.deviceTemplates.filter(
      (temp) => temp.id !== templateId
    );
    await saveDeviceSettings(newSettings);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Retrieves the updated category and template IDs based on matches found between local and shared device settings.
 * This function first attempts to find a category in the shared settings that matches either the ID or the name
 * of a category specified in the local settings. It then attempts to find a matching template within the found category
 * by either ID or name. If no valid matches are found for either the category or the template, it assigns predefined
 * unknown IDs.
 *
 * @param {string} categoryId - The ID of the category to be located in the local device settings.
 * @param {string} templateId - The ID of the template to be located within the found category in the local device settings.
 * @param {DeviceSettingsJson} localDeviceSettings - A JSON object containing device settings specific to the local device,
 * including categories and templates.
 * @param {DeviceSettingsJson} sharedDeviceSettings - A JSON object containing device settings shared across devices,
 * including categories and templates.
 * @param {DevicePowerUsageType} [devicePowerUsageType] - An optional parameter specifying the type of power usage for the device,
 * used to determine unknown template IDs when needed.
 * @returns {Object} An object containing `updatedCategoryId` and `updatedTemplateId`. These are the IDs for the matched
 * or default category and template in the shared settings. If no matches are found, predefined unknown IDs are used.
 */
export function getUpdatedCategoryAndTemplateIds(
  categoryId: string,
  templateId: string,
  localDeviceSettings: DeviceSettingsJson,
  sharedDeviceSettings: DeviceSettingsJson,
  devicePowerUsageType?: DevicePowerUsageType,
) {
  const category = localDeviceSettings.deviceCategories.find((cat) => {
    return cat.id === categoryId;
  });
  const categoryName = category?.categoryName;

  const updatedCategory = sharedDeviceSettings.deviceCategories.find((cat) => {
    return cat.categoryName === categoryName ||
      cat.id === categoryId;
  });
  const updatedCategoryId = updatedCategory?.id;

  const template = category?.deviceTemplates.find((temp) => {
    return temp.id === templateId;
  });
  const templateName = template?.deviceName;

  const updatedTemplate = updatedCategory?.deviceTemplates.find((temp) => {
    return temp.deviceName === templateName ||
      temp.id === templateId;
  });
  const updatedTemplateId = updatedTemplate?.id;

  const shouldAssignUnknownId = !updatedCategoryId || !updatedTemplateId;
  const templateUnknownId = template 
    ? getUnknowTemplateId(getDeviceAttributeTypeFromDeviceTemplate(template))
    : devicePowerUsageType
      ? getUnknowTemplateId(devicePowerUsageType)
      : UNKNOWN_TEMPLATE_1_ID;

  return {
    updatedCategoryId: shouldAssignUnknownId ? CATEGORY_UNKNOWN_ID : updatedCategoryId,
    updatedTemplateId: shouldAssignUnknownId ? templateUnknownId : updatedTemplateId
  };
}